/**
 * Seiteneffekte zu Gateway-Ereignissen (nach dem Reducer): Query-Invalidierung, In-App-Benachrichtigungen,
 * Töne, Super-Reaktion-Animationen, Desktop-Benachrichtigungen.
 */
import type { ServerEventName, ServerEventPayload } from '@lagerfeuer/shared';
import { queryClient, qk } from '../lib/queryClient';
import { pathForChannel } from '../lib/routes';
import { playNotificationSound } from '../lib/sound';
import { appState } from '../store/app';
import { useUi } from '../store/ui';
import { isChannelOpen } from './bus';

/** Aktuell fokussierter Kanal (für Benachrichtigungen ohne Ton im offenen Kanal). */
let focusedChannelId: string | null = null;
export function setFocusedChannel(id: string | null): void {
  focusedChannelId = id;
}
export function getFocusedChannel(): string | null {
  return focusedChannelId;
}

/**
 * Nach einem erneuten READY: Mitglieder-/Benachrichtigungs-/Thread-Abfragen erneuern (Ereignisse während der
 * Trennung fehlen) und Nachrichten-Ausschnitte nicht angezeigter Kanäle verwerfen (sie werden beim Öffnen neu
 * geladen). Angezeigte Kanäle laden selbst nach (MessageList, `onResync`).
 */
export function handleResync(): void {
  appState().update((d) => {
    for (const id of Object.keys(d.messages)) if (!isChannelOpen(id)) delete d.messages[id];
  });
  void queryClient.invalidateQueries({ queryKey: ['members'] });
  void queryClient.invalidateQueries({ queryKey: qk.notifications });
  void queryClient.invalidateQueries({ queryKey: ['threads'] });
  void queryClient.invalidateQueries({ queryKey: ['thread-members'] });
  void queryClient.invalidateQueries({ queryKey: ['pins'] });
}

export function handleSideEffects<E extends ServerEventName>(event: E, payload: ServerEventPayload<E>): void {
  switch (event) {
    case 'SERVER_BAN_ADD':
    case 'SERVER_BAN_REMOVE': {
      const p = payload as ServerEventPayload<'SERVER_BAN_ADD'>;
      void queryClient.invalidateQueries({ queryKey: qk.bans(p.serverId) });
      return;
    }
    case 'INVITE_CREATE': {
      const p = payload as ServerEventPayload<'INVITE_CREATE'>;
      void queryClient.invalidateQueries({ queryKey: qk.invites(p.server.id) });
      return;
    }
    case 'INVITE_DELETE': {
      const p = payload as ServerEventPayload<'INVITE_DELETE'>;
      void queryClient.invalidateQueries({ queryKey: qk.invites(p.serverId) });
      return;
    }
    case 'CHANNEL_PINS_UPDATE': {
      const p = payload as ServerEventPayload<'CHANNEL_PINS_UPDATE'>;
      void queryClient.invalidateQueries({ queryKey: qk.pins(p.channelId) });
      return;
    }
    case 'THREAD_CREATE':
    case 'THREAD_UPDATE':
    case 'THREAD_DELETE': {
      const p = payload as { parentId: string | null };
      if (p.parentId) void queryClient.invalidateQueries({ queryKey: ['threads', p.parentId] });
      return;
    }
    case 'THREAD_MEMBERS_UPDATE': {
      const p = payload as ServerEventPayload<'THREAD_MEMBERS_UPDATE'>;
      void queryClient.invalidateQueries({ queryKey: qk.threadMembers(p.threadId) });
      return;
    }
    case 'MESSAGE_REACTION_ADD': {
      const p = payload as ServerEventPayload<'MESSAGE_REACTION_ADD'>;
      void queryClient.invalidateQueries({ queryKey: qk.reactors(p.messageId, p.reaction.key) });
      if (p.isSuper && !appState().settings?.reducedMotion) {
        useUi.getState().burst({ messageId: p.messageId, emoji: p.reaction.emoji });
      }
      return;
    }
    case 'NOTIFICATION_CREATE': {
      const n = payload as ServerEventPayload<'NOTIFICATION_CREATE'>;
      void queryClient.invalidateQueries({ queryKey: qk.notifications });
      // Erinnerungen zeigt das Paket Extras als eigenen Toast (mit „Erledigt"), hier nur noch Ton/Desktop.
      const reminder = n.type === 'REMINDER';
      if (!reminder && n.channelId && n.channelId === focusedChannelId && document.hasFocus()) return;
      const st = appState();
      const ch = n.channelId ? st.channels[n.channelId] : undefined;
      const to = ch ? pathForChannel(ch, n.messageId ?? undefined) : undefined;
      if (!reminder) useUi.getState().pushToast({ kind: 'notification', title: n.title, body: n.body ?? undefined, to, iconUrl: n.actor?.avatarUrl ?? null });
      // „Bitte nicht stören": nur der stille In-App-Hinweis, keine Töne/Desktop-Benachrichtigungen.
      const dnd = st.me?.status === 'DND';
      if (!dnd && st.settings?.notifySound !== false) playNotificationSound();
      if (!dnd && st.settings?.notifyDesktop && !document.hasFocus() && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const desk = new Notification(n.title, { body: n.body ?? undefined, icon: n.actor?.avatarUrl ?? '/icons/icon-192.png', tag: n.id });
          desk.onclick = () => {
            window.focus();
            if (to) window.history.pushState({}, '', to);
            window.dispatchEvent(new PopStateEvent('popstate'));
          };
        } catch {
          // Manche Browser erlauben `new Notification` nur im Service Worker – dann bleibt es beim Toast.
        }
      }
      return;
    }
    case 'USER_UPDATE': {
      // Profil-Popout liest zuerst die (60 s gecachte) Profil-Abfrage → nach Profiländerungen neu laden.
      const u = payload as ServerEventPayload<'USER_UPDATE'>;
      void queryClient.invalidateQueries({ queryKey: ['profile', u.id] });
      return;
    }
    case 'SERVER_MEMBER_UPDATE': {
      const m = payload as ServerEventPayload<'SERVER_MEMBER_UPDATE'>;
      void queryClient.invalidateQueries({ queryKey: qk.profile(m.user.id, m.serverId) });
      return;
    }
    default:
      return;
  }
}
